import { useState } from 'react'
import { router } from '@inertiajs/react'
import type { DateRange } from '../types'

type NavigateOptions = NonNullable<Parameters<typeof router.get>[2]>
type TelemetryParamValue = string | number | undefined
type TelemetryParams = Record<string, TelemetryParamValue>

function cleanTelemetryParams(params: TelemetryParams) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
}

export function telemetryRangeParams(dateFrom: string, dateTo: string, dateRange: DateRange): TelemetryParams {
  if (dateRange === 'all') return { date_range: 'all' }
  if (dateRange === 'custom') return { date_from: dateFrom || undefined, date_to: dateTo || undefined }
  return {}
}

export function telemetryPathWithParams(path: string, params: TelemetryParams) {
  const cleanParams = cleanTelemetryParams(params)
  const query = new URLSearchParams(cleanParams as Record<string, string>).toString()
  return query ? `${path}?${query}` : path
}

export function useTelemetryNavigation(basePath: string, defaults: TelemetryParams) {
  const navigateWithParams = (
    overrides: TelemetryParams,
    options: NavigateOptions = {}
  ) => {
    const params = { ...defaults, ...overrides }
    const cleanParams = cleanTelemetryParams(params)
    router.get(basePath, cleanParams, { preserveState: true, ...options })
  }

  const handleSort = (currentSortBy: string, currentSortDirection: string, sortKey: string) => {
    const newDirection = currentSortBy === sortKey
      ? (currentSortDirection === 'asc' ? 'desc' : 'asc')
      : 'desc'
    navigateWithParams({ sort_by: sortKey, sort_direction: newDirection, page: 1 })
  }

  const handleDateChange = (params: { date_from?: string; date_to?: string; date_range?: string }) => {
    navigateWithParams({ ...params, page: 1 })
  }

  return { navigateWithParams, handleSort, handleDateChange }
}

export function useSearchQuery(initial: string) {
  return useState(initial)
}
